<script>
// utils and composables
import { login } from '../../api/auth';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';
import axios from 'axios';
import { setAuthCredentials } from 'dashboard/store/utils/api';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';
import mipLogo from '../../../images/mip-logo@3x.png';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    NextButton,
    SimpleDivider,
    MfaVerification,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      mipLogo,
      activeTab: 'phone', // 'email' or 'phone' (phonePassword 已隐藏)

      // 邮箱登录凭据
      emailCredentials: {
        email: '',
        password: '',
      },

      // 手机号验证码登录凭据
      phoneCodeCredentials: {
        phone: '',
        code: '',
        agreedToTerms: false,
      },

      // 手机号密码登录凭据
      phonePasswordCredentials: {
        phone: '',
        password: '',
      },

      countdown: 0,

      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,

      // 重置密码状态
      showResetForm: false,
      resetCredentials: {
        phone: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
      },
      resetCountdown: 0,
    };
  },
  validations() {
    return {
      emailCredentials: {
        password: { required },
        email: { required, email },
      },
      phoneCodeCredentials: {
        phone: { required },
        code: { required },
      },
      phonePasswordCredentials: {
        phone: { required },
        password: { required },
      },
      resetCredentials: {
        phone: { required },
        code: { required },
        newPassword: { required },
        confirmPassword: { required },
      },
    };
  },
  computed: {
    globalConfig() {
      return window.chatwootConfig || {};
    },
    allowedLoginMethods() {
      return window.chatwootConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.chatwootConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.chatwootConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
    // 检查当前表单是否有效
    isFormValid() {
      if (this.activeTab === 'email') {
        return !this.v$.emailCredentials.$invalid;
      }
      if (this.activeTab === 'phone') {
        return !this.v$.phoneCodeCredentials.$invalid;
      }
      if (this.activeTab === 'phonePassword') {
        return !this.v$.phonePasswordCredentials.$invalid;
      }
      return false;
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      const translatedMessage = this.getTranslatedMessage(messageKey);
      useAlert(translatedMessage);
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    getTranslatedMessage(key) {
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },

    // 切换Tab并清理状态
    switchTab(tab) {
      this.activeTab = tab;
      // 清理所有状态
      this.showResetForm = false;
      this.resetCredentials = {
        phone: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
      };
      this.phoneCodeCredentials.agreedToTerms = false;
      this.countdown = 0;
      this.resetCountdown = 0;
    },
    handleImpersonation() {
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },

    // 发送验证码
    async sendCode() {
      if (!this.phoneCodeCredentials.phone) {
        useAlert(this.$t('LOGIN.PHONE.PHONE_REQUIRED'));
        return;
      }

      try {
        await axios.post('/api/v1/phone_auth/send_code', {
          phone_number: this.phoneCodeCredentials.phone,
        });

        // 开始倒计时
        this.countdown = 60;
        const timer = setInterval(() => {
          this.countdown--;
          if (this.countdown <= 0) {
            clearInterval(timer);
          }
        }, 1000);

        useAlert(this.$t('LOGIN.PHONE.SEND_SUCCESS'));
      } catch (error) {
        useAlert(
          error.response?.data?.error || this.$t('LOGIN.PHONE.SEND_FAILED')
        );
      }
    },

    // 邮箱登录
    submitEmailLogin() {
      // SSO登录时跳过验证（不需要密码）
      if (!this.ssoAuthToken && this.v$.emailCredentials.$invalid) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.emailCredentials.email,
        password: this.emailCredentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },

    // 手机号验证码登录
    async submitPhoneCodeLogin() {
      if (this.v$.phoneCodeCredentials.$invalid) {
        useAlert(this.$t('LOGIN.PHONE.PHONE_REQUIRED'));
        return;
      }

      if (!this.phoneCodeCredentials.agreedToTerms) {
        useAlert('请阅读并同意用户协议');
        return;
      }

      this.loginApi.showLoading = true;
      try {
        const response = await axios.post(
          '/api/v1/phone_auth/login_or_register',
          {
            phone_number: this.phoneCodeCredentials.phone,
            code: this.phoneCodeCredentials.code,
            agreed_to_terms: this.phoneCodeCredentials.agreedToTerms,
            action: 'login',
          }
        );

        // 保存认证信息
        setAuthCredentials(response);

        useAlert(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        setTimeout(() => {
          window.location = window.chatwootConfig.redirectURL || '/app';
        }, 500);
      } catch (error) {
        this.loginApi.hasErrored = true;
        // 优先显示后端返回的错误信息
        const errorMsg =
          error.response?.data?.error || error.response?.data?.message;
        useAlert(errorMsg || this.$t('LOGIN.API.ERROR_MESSAGE'));
      } finally {
        this.loginApi.showLoading = false;
      }
    },

    // 手机号密码登录
    async submitPhonePasswordLogin() {
      if (this.v$.phonePasswordCredentials.$invalid) {
        useAlert(this.$t('LOGIN.PHONE.PHONE_REQUIRED'));
        return;
      }

      this.loginApi.showLoading = true;
      try {
        const response = await axios.post('/api/v1/phone_auth/password_login', {
          phone_number: this.phonePasswordCredentials.phone,
          password: this.phonePasswordCredentials.password,
        });

        // 保存认证信息
        setAuthCredentials(response);

        useAlert(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        setTimeout(() => {
          window.location = window.chatwootConfig.redirectURL || '/app';
        }, 500);
      } catch (error) {
        this.loginApi.hasErrored = true;
        // 优先显示后端返回的错误信息
        const errorMsg =
          error.response?.data?.error || error.response?.data?.message;
        useAlert(errorMsg || this.$t('LOGIN.API.UNAUTH'));
      } finally {
        this.loginApi.showLoading = false;
      }
    },

    submitFormLogin() {
      if (this.loginApi.showLoading) return;

      if (this.activeTab === 'email') {
        this.submitEmailLogin();
      } else if (this.activeTab === 'phone') {
        this.submitPhoneCodeLogin();
      } else if (this.activeTab === 'phonePassword') {
        this.submitPhonePasswordLogin();
      }
    },

    submitLogin() {
      // 保留原有的 SSO 登录逻辑
      this.submitEmailLogin();
    },

    handleMfaVerified() {
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      this.mfaRequired = false;
      this.mfaToken = null;
      this.emailCredentials.password = '';
    },

    // 重置密码相关方法
    startResetPassword() {
      this.showResetForm = true;
      this.resetCredentials = {
        phone: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
      };
    },

    cancelReset() {
      this.showResetForm = false;
      this.resetCredentials = {
        phone: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
      };
    },

    async sendResetCode() {
      if (!this.resetCredentials.phone) {
        useAlert(this.$t('LOGIN.PHONE.PHONE_REQUIRED'));
        return;
      }

      try {
        await axios.post('/api/v1/phone_auth/send_code', {
          phone_number: this.resetCredentials.phone,
        });

        this.resetCountdown = 60;
        const timer = setInterval(() => {
          this.resetCountdown--;
          if (this.resetCountdown <= 0) {
            clearInterval(timer);
          }
        }, 1000);

        useAlert(this.$t('LOGIN.PHONE.SEND_SUCCESS'));
      } catch (error) {
        useAlert(
          error.response?.data?.error || this.$t('LOGIN.PHONE.SEND_FAILED')
        );
      }
    },

    async resetPassword() {
      // 验证所有字段
      if (
        !this.resetCredentials.phone ||
        !this.resetCredentials.code ||
        !this.resetCredentials.newPassword ||
        !this.resetCredentials.confirmPassword
      ) {
        useAlert('请填写所有字段');
        return;
      }

      if (
        this.resetCredentials.newPassword !==
        this.resetCredentials.confirmPassword
      ) {
        useAlert(this.$t('LOGIN.PASSWORD.MISMATCH'));
        return;
      }

      try {
        await axios.post('/api/v1/phone_auth/reset_password_by_code', {
          phone_number: this.resetCredentials.phone,
          code: this.resetCredentials.code,
          new_password: this.resetCredentials.newPassword,
        });

        useAlert(this.$t('LOGIN.RESET_PASSWORD.SUCCESS_MESSAGE'));
        this.cancelReset();
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('LOGIN.RESET_PASSWORD.CODE_INVALID')
        );
      }
    },
  },
};
</script>

<template>
  <main
    class="flex flex-col w-full min-h-screen py-20 bg-n-brand/5 dark:bg-n-background sm:px-6 lg:px-8"
  >
    <section class="max-w-5xl mx-auto">
      <!-- Logo 图片和标题 - 左右结构 -->
      <div class="flex items-center justify-center gap-4 mb-6">
        <img
          :src="mipLogo"
          alt="MIP Logo"
          class="h-12 w-auto object-contain"
        />
        <h1 class="text-2xl font-bold text-n-slate-12">AI客服云</h1>
      </div>
      <h2 class="mt-6 text-3xl font-medium text-center text-n-slate-12">
        {{ replaceInstallationName($t('LOGIN.TITLE')) }}
      </h2>
      <!-- 隐藏创建新账户链接，因为手机验证码可自动注册 -->
      <!-- <p v-if="showSignupLink" class="mt-3 text-sm text-center text-n-slate-11">
        {{ $t('COMMON.OR') }}
        <router-link to="auth/signup" class="lowercase text-link text-n-brand">
          {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
        </router-link>
      </p> -->
    </section>

    <!-- MFA Verification Section -->
    <section v-if="mfaRequired" class="mt-11">
      <MfaVerification
        :mfa-token="mfaToken"
        @verified="handleMfaVerified"
        @cancel="handleMfaCancel"
      />
    </section>

    <!-- Regular Login Section -->
    <section
      v-else
      class="bg-white shadow sm:mx-auto mt-11 sm:w-full sm:max-w-lg dark:bg-n-solid-2 p-11 sm:shadow-lg sm:rounded-lg"
      :class="{
        'mb-8 mt-15': !showGoogleOAuth,
        'animate-wiggle': loginApi.hasErrored,
      }"
    >
      <div v-if="!email">
        <!-- Tab 切换 -->
        <div
          v-if="!showGoogleOAuth && !showSamlLogin"
          class="mb-6 flex border-b border-n-container"
        >
          <button
            class="px-4 py-2 font-medium text-sm transition-colors flex-1"
            :class="[
              activeTab === 'email'
                ? 'border-b-2 border-n-brand text-n-brand'
                : 'text-n-slate-11 hover:text-n-slate-12',
            ]"
            @click="switchTab('email')"
          >
            {{ $t('LOGIN.EMAIL_TAB') }}
          </button>
          <button
            class="px-4 py-2 font-medium text-sm transition-colors flex-1"
            :class="[
              activeTab === 'phone'
                ? 'border-b-2 border-n-brand text-n-brand'
                : 'text-n-slate-11 hover:text-n-slate-12',
            ]"
            @click="switchTab('phone')"
          >
            {{ $t('LOGIN.PHONE_TAB') }}
          </button>
          <!-- 隐藏密码登录 tab -->
          <!-- <button
            :class="[
              'px-4 py-2 font-medium text-sm transition-colors flex-1',
              activeTab === 'phonePassword'
                ? 'border-b-2 border-n-brand text-n-brand'
                : 'text-n-slate-11 hover:text-n-slate-12'
            ]"
            @click="switchTab('phonePassword')"
          >
            {{ $t('LOGIN.PHONE_PASSWORD_TAB') }}
          </button> -->
        </div>

        <div
          v-if="showGoogleOAuth || showSamlLogin"
          class="flex flex-col gap-4"
        >
          <GoogleOAuthButton v-if="showGoogleOAuth" />
          <div v-if="showSamlLogin" class="text-center">
            <router-link
              to="/app/login/sso"
              class="inline-flex justify-center w-full px-4 py-3 items-center bg-n-background dark:bg-n-solid-3 rounded-md shadow-sm ring-1 ring-inset ring-n-container dark:ring-n-container focus:outline-offset-0 hover:bg-n-alpha-2 dark:hover:bg-n-alpha-2"
            >
              <Icon
                icon="i-lucide-lock-keyhole"
                class="size-5 text-n-slate-11"
              />
              <span class="ml-2 text-base font-medium text-n-slate-12">
                {{ $t('LOGIN.SAML.LABEL') }}
              </span>
            </router-link>
          </div>
          <SimpleDivider :label="$t('COMMON.OR')" class="uppercase" />

          <!-- 第三方登录后也显示 Tab 切换 -->
          <div class="mb-4 flex border-b border-n-container">
            <button
              class="px-4 py-2 font-medium text-sm transition-colors flex-1"
              :class="[
                activeTab === 'email'
                  ? 'border-b-2 border-n-brand text-n-brand'
                  : 'text-n-slate-11 hover:text-n-slate-12',
              ]"
              @click="switchTab('email')"
            >
              {{ $t('LOGIN.EMAIL_TAB') }}
            </button>
            <button
              class="px-4 py-2 font-medium text-sm transition-colors flex-1"
              :class="[
                activeTab === 'phone'
                  ? 'border-b-2 border-n-brand text-n-brand'
                  : 'text-n-slate-11 hover:text-n-slate-12',
              ]"
              @click="switchTab('phone')"
            >
              {{ $t('LOGIN.PHONE_TAB') }}
            </button>
            <!-- 隐藏密码登录 tab -->
            <!-- <button
              :class="[
                'px-4 py-2 font-medium text-sm transition-colors flex-1',
                activeTab === 'phonePassword'
                  ? 'border-b-2 border-n-brand text-n-brand'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              ]"
              @click="switchTab('phonePassword')"
            >
              {{ $t('LOGIN.PHONE_PASSWORD_TAB') }}
            </button> -->
          </div>
        </div>

        <!-- 邮箱登录表单 -->
        <form
          v-if="activeTab === 'email'"
          class="space-y-5"
          @submit.prevent="submitFormLogin"
        >
          <FormInput
            v-model="emailCredentials.email"
            name="email_address"
            type="text"
            data-testid="email_input"
            :tabindex="1"
            required
            :label="$t('LOGIN.EMAIL.LABEL')"
            :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
            :has-error="v$.emailCredentials.email.$error"
            @input="v$.emailCredentials.email.$touch"
          />
          <FormInput
            v-model="emailCredentials.password"
            type="password"
            name="password"
            data-testid="password_input"
            required
            :tabindex="2"
            :label="$t('LOGIN.PASSWORD.LABEL')"
            :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
            :has-error="v$.emailCredentials.password.$error"
            @input="v$.emailCredentials.password.$touch"
          >
            <p v-if="!globalConfig.disableUserProfileUpdate">
              <router-link
                to="auth/reset/password"
                class="text-sm text-link"
                tabindex="4"
              >
                {{ $t('LOGIN.FORGOT_PASSWORD') }}
              </router-link>
            </p>
          </FormInput>
          <NextButton
            lg
            type="submit"
            data-testid="submit_button"
            class="w-full"
            :tabindex="3"
            :label="$t('LOGIN.EMAIL_SUBMIT')"
            :disabled="loginApi.showLoading"
            :is-loading="loginApi.showLoading"
          />
        </form>

        <!-- 手机号验证码登录表单 -->
        <form
          v-if="activeTab === 'phone'"
          class="space-y-5"
          @submit.prevent="submitFormLogin"
        >
          <FormInput
            v-model="phoneCodeCredentials.phone"
            type="tel"
            name="phone_number"
            :label="$t('LOGIN.PHONE.PHONE_LABEL')"
            :placeholder="$t('LOGIN.PHONE.PHONE_PLACEHOLDER')"
            :has-error="v$.phoneCodeCredentials.phone.$error"
            @input="v$.phoneCodeCredentials.phone.$touch"
          />
          <div class="flex gap-2">
            <div class="flex-1 w-full">
              <FormInput
                v-model="phoneCodeCredentials.code"
                type="text"
                name="verification_code"
                :label="$t('LOGIN.PHONE.CODE_LABEL')"
                :placeholder="$t('LOGIN.PHONE.CODE_PLACEHOLDER')"
                :has-error="v$.phoneCodeCredentials.code.$error"
                @input="v$.phoneCodeCredentials.code.$touch"
              />
            </div>
            <button
              type="button"
              :disabled="countdown > 0 || !phoneCodeCredentials.phone"
              class="px-4 py-2 mt-6 text-sm font-medium text-white bg-n-brand rounded-md hover:bg-n-brand/80 disabled:bg-n-slate-3 disabled:cursor-not-allowed transition-colors min-w-[100px] shrink-0"
              @click="sendCode"
            >
              {{
                countdown > 0 ? `${countdown}s` : $t('LOGIN.PHONE.SEND_CODE')
              }}
            </button>
          </div>
          <div class="flex items-start gap-2 mt-2">
            <input
              id="agree-terms-phone"
              v-model="phoneCodeCredentials.agreedToTerms"
              type="checkbox"
              name="agree_terms"
              class="mt-1 h-4 w-4 rounded border-n-slate-6 text-n-brand focus:ring-n-brand"
            />
            <label
              for="agree-terms-phone"
              class="text-sm text-n-slate-11 leading-snug"
            >
              我已阅读并同意
              <a
                href="https://www.chatwoot.com/terms"
                target="_blank"
                class="text-link"
                >用户协议</a>
              和
              <a
                href="https://www.chatwoot.com/privacy-policy"
                target="_blank"
                class="text-link"
                >隐私政策</a>
            </label>
          </div>
          <NextButton
            lg
            type="submit"
            class="w-full"
            :label="$t('LOGIN.SUBMIT')"
            :disabled="loginApi.showLoading"
            :is-loading="loginApi.showLoading"
          />
        </form>

        <!-- 手机号密码登录表单 -->
        <form
          v-if="activeTab === 'phonePassword' && !showResetForm"
          class="space-y-5"
          @submit.prevent="submitFormLogin"
        >
          <FormInput
            v-model="phonePasswordCredentials.phone"
            type="tel"
            name="phone_number"
            :label="$t('LOGIN.PHONE.PHONE_LABEL')"
            :placeholder="$t('LOGIN.PHONE.PHONE_PLACEHOLDER')"
            :has-error="v$.phonePasswordCredentials.phone.$error"
            @input="v$.phonePasswordCredentials.phone.$touch"
          />
          <FormInput
            v-model="phonePasswordCredentials.password"
            type="password"
            name="phone_password"
            :label="$t('LOGIN.PASSWORD.LABEL')"
            :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
            :has-error="v$.phonePasswordCredentials.password.$error"
            @input="v$.phonePasswordCredentials.password.$touch"
          >
            <p class="text-sm text-link">
              <button
                type="button"
                class="text-link"
                @click="startResetPassword"
              >
                {{ $t('LOGIN.FORGOT_PASSWORD') }}
              </button>
            </p>
          </FormInput>
          <NextButton
            lg
            type="submit"
            class="w-full"
            :label="$t('LOGIN.SUBMIT')"
            :disabled="loginApi.showLoading"
            :is-loading="loginApi.showLoading"
          />
        </form>

        <!-- 重置密码表单 -->
        <form v-if="showResetForm" class="space-y-5" @submit.prevent>
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ $t('LOGIN.RESET.TITLE') }}
          </h3>
          <FormInput
            v-model="resetCredentials.phone"
            type="tel"
            name="reset_phone"
            :label="$t('LOGIN.PHONE.PHONE_LABEL')"
            :placeholder="$t('LOGIN.PHONE.PHONE_PLACEHOLDER')"
            :has-error="v$.resetCredentials.phone.$error"
            @input="v$.resetCredentials.phone.$touch"
          />
          <div class="flex gap-2">
            <div class="flex-1 w-full">
              <FormInput
                v-model="resetCredentials.code"
                type="text"
                name="reset_code"
                :label="$t('LOGIN.PHONE.CODE_LABEL')"
                :placeholder="$t('LOGIN.PHONE.CODE_PLACEHOLDER')"
                :has-error="v$.resetCredentials.code.$error"
                @input="v$.resetCredentials.code.$touch"
              />
            </div>
            <button
              type="button"
              :disabled="resetCountdown > 0 || !resetCredentials.phone"
              class="px-4 py-2 mt-6 text-sm font-medium text-white bg-n-brand rounded-md hover:bg-n-brand/80 disabled:bg-n-slate-3 disabled:cursor-not-allowed transition-colors min-w-[100px] shrink-0"
              @click="sendResetCode"
            >
              {{
                resetCountdown > 0
                  ? `${resetCountdown}s`
                  : $t('LOGIN.PHONE.SEND_CODE')
              }}
            </button>
          </div>
          <FormInput
            v-model="resetCredentials.newPassword"
            type="password"
            name="new_password"
            :label="$t('LOGIN.PASSWORD.NEW_LABEL')"
            :placeholder="$t('LOGIN.PASSWORD.NEW_PLACEHOLDER')"
            :has-error="v$.resetCredentials.newPassword.$error"
            @input="v$.resetCredentials.newPassword.$touch"
          />
          <FormInput
            v-model="resetCredentials.confirmPassword"
            type="password"
            name="confirm_password"
            :label="$t('LOGIN.PASSWORD.CONFIRM_LABEL')"
            :placeholder="$t('LOGIN.PASSWORD.CONFIRM_PLACEHOLDER')"
            :has-error="v$.resetCredentials.confirmPassword.$error"
            @input="v$.resetCredentials.confirmPassword.$touch"
          />
          <NextButton
            lg
            type="button"
            class="w-full"
            :label="$t('LOGIN.RESET.CONFIRM_BUTTON')"
            @click="resetPassword"
          />
          <button
            type="button"
            class="w-full text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="cancelReset"
          >
            {{ $t('LOGIN.RESET.BACK_TO_LOGIN') }}
          </button>
        </form>
      </div>
      <div v-else class="flex items-center justify-center">
        <Spinner color-scheme="primary" size="" />
      </div>
    </section>
  </main>
</template>
